import React from "react";
import StaticPage from "./StaticPage";

export default function PrivacyPolicy() {
  return (
    <StaticPage
      eyebrow="Legal"
      title="Privacy Policy"
      updated="February 2026"
      testId="page-privacy"
    >
      <p>
        This Privacy Policy describes how <strong>Occuthera</strong> (&quot;Occuthera,&quot; &quot;we,&quot;
        &quot;us&quot;) collects, uses, and safeguards information in connection with the SenseWise
        web application (the &quot;Service&quot;). SenseWise is a sensory self-regulation companion
        designed for use by occupational therapists (OTs), educators, students, and families.
      </p>

      <h2>1. Information we collect</h2>
      <p><strong>Account information (therapists).</strong> When a therapist creates an account we collect
        name, email address, password (stored as a bcrypt hash — never in plain text), and an optional
        organization / district name.</p>
      <p><strong>Student profile information.</strong> A therapist may create a roster of student
        accounts. For each student we store a display name, a short login username, a hashed PIN,
        an OT-authored sensory profile (seek / avoid / neutral per system), and the list of intervention
        bubbles the therapist has enabled.</p>
      <p><strong>Selection logs.</strong> When a signed-in student taps a bubble, the Service records the
        intervention chosen, the sensory system, and a timestamp. Selections in self-serve
        (&quot;Explore&quot;) mode are <strong>not</strong> tied to any account and are not stored server-side.</p>
      <p><strong>Cookies.</strong> The Service uses two HTTP-only cookies (access + refresh tokens) to
        keep users signed in. We do not use third-party advertising or tracking cookies.</p>

      <h2>2. How we use information</h2>
      <ul>
        <li>To authenticate users and secure the Service.</li>
        <li>To display curated intervention sets and generate therapist reports.</li>
        <li>To operate, maintain, and improve the Service.</li>
        <li>To respond to support requests.</li>
      </ul>
      <p>We do <strong>not</strong> sell personal information. We do not use student data for advertising,
        for training third-party AI models, or for any purpose beyond providing the Service.</p>

      <h2>3. FERPA and student data (US school districts)</h2>
      <p>SenseWise is intended to be deployed as a &quot;school official&quot; tool under the Family
        Educational Rights and Privacy Act (FERPA), 20 U.S.C. § 1232g. When a school district engages
        Occuthera under a written services agreement or Data Processing Addendum (DPA), Occuthera acts
        as a school official with a legitimate educational interest, under the direct control of the
        district with respect to the use and maintenance of any education records the district
        provides.</p>
      <p>Districts remain the data controller of student personally identifiable information (PII). To
        request a DPA or California Student Data Privacy Agreement (CSDPA), contact us at
        <a href="mailto:Hello@occuthera.com"> Hello@occuthera.com</a>.</p>

      <h2>4. COPPA (children under 13)</h2>
      <p>SenseWise does not collect personal information directly from children. Therapist / district
        accounts create student profiles on behalf of children. Where SenseWise is used with children
        under 13, the school or district acts as the parent&apos;s agent under the Children&apos;s
        Online Privacy Protection Act (COPPA), 15 U.S.C. §§ 6501–6506, and consents on behalf of
        parents for the internal educational use of the Service.</p>

      <h2>5. Data retention &amp; deletion</h2>
      <ul>
        <li>Therapists may delete any student profile from within the app. Deletion removes the
          student record and all associated selection logs.</li>
        <li>If a therapist account is closed, we will delete or de-identify associated student profiles
          within 30 days of a written request.</li>
        <li>De-identified, aggregated data (e.g., total selections per system across all users) may be
          retained indefinitely for product improvement.</li>
      </ul>

      <h2>6. Security</h2>
      <p>Passwords and student PINs are hashed using bcrypt. Session tokens are transmitted over HTTPS
        and stored in HTTP-only cookies. Access to production systems is restricted to authorized
        personnel. No system is 100% secure; we notify affected users promptly of any confirmed
        material breach as required by applicable law.</p>

      <h2>7. Your rights</h2>
      <p>Depending on your jurisdiction you may have the right to access, correct, delete, or receive a
        copy of your personal information. To exercise these rights contact
        <a href="mailto:Hello@occuthera.com"> Hello@occuthera.com</a>.</p>

      <h2>8. International transfers</h2>
      <p>SenseWise is operated from the United States. If you access the Service from outside the U.S.
        you consent to the transfer and processing of your information in the U.S.</p>

      <h2>9. Changes to this policy</h2>
      <p>We may update this Privacy Policy from time to time. Material changes will be posted here and
        the &quot;Last updated&quot; date above will be revised.</p>

      <h2>10. Contact</h2>
      <p>
        Occuthera<br />
        Email: <a href="mailto:Hello@occuthera.com">Hello@occuthera.com</a><br />
        Web: <a href="https://occuthera.com" target="_blank" rel="noreferrer">occuthera.com</a>
      </p>
    </StaticPage>
  );
}
